import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button, { ButtonProps } from '@mui/material/Button';
import Stack, { StackProps } from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';

type CopyPropOptions = {
  copyValue?: string;
  disable?: boolean;
  icon?: ReactElement;
  showLabel?: boolean;
  stackProps?: StackProps;
  alignItems?: StackProps['alignItems'];
  // requiring children to be a single React element or string if provided
  children?: ReactElement | string;
  dataTestId?: string;
  variant?: ButtonProps['variant'];
  color?: ButtonProps['color'];
  fullWidth?: boolean;
};

const Copy = ({
  children = undefined,
  copyValue = undefined,
  disable = false,
  icon = undefined,
  showLabel = false,
  stackProps = {
    direction: { xs: 'column', sm: 'row' },
    spacing: { sm: 1 },
    alignItems: 'baseline',
  },
  dataTestId = 'copy-btn',
  variant = 'text',
  color = 'inherit',
  fullWidth = false,
}: CopyPropOptions) => {
  const { t } = useTranslation('site', { keyPrefix: 'Copy' });
  const [copied, setCopied] = useState(false);

  const childrenIsElement = typeof children === 'object';
  const childrenText = childrenIsElement ? children?.props?.children : children;

  const handleCopy = () => {
    const findCopy = copyValue || childrenText;

    try {
      navigator.clipboard.writeText(findCopy as string);
      setCopied(true);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }

    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Stack {...stackProps}>
      {children}
      <Tooltip open={copied} title={t('copiedMsg')} placement="top-end">
        <Button
          variant={variant}
          color={color}
          fullWidth={fullWidth}
          sx={{
            minWidth: fullWidth ? undefined : icon ? 0 : '40px',
            ...(icon &&
              showLabel && {
                gap: 0.5,
              }),
          }}
          disabled={disable}
          onClick={handleCopy}
          data-testid={dataTestId}
        >
          {icon}
          {(!icon || showLabel) && t('copyBtn')}
        </Button>
      </Tooltip>
    </Stack>
  );
};

export default Copy;
